import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ScoredGeography } from '@/types/insights';
import type { ReportFilters } from '@/types/report';
import { describeFilters } from '@/reports/filterSummary';
import { round } from '@/utils/numbers';

const TITLE = 'Texas Market Trends Report';

/**
 * Generates the Report Builder's PDF export: title, filter summary,
 * timestamp, a table of every matching county, and a short observations
 * block. Mirrors the existing exportSummaryPdf style in pdfExport.ts so the
 * two PDF flavors (quick "export what's on screen" vs. the new filtered
 * Report Builder) look like the same product.
 */
export function exportReportPdf(rows: ScoredGeography[], filters: ReportFilters, filename = 'texas-market-trends-report.pdf') {
  const doc = new jsPDF();

  doc.setFontSize(16);
  doc.text(TITLE, 14, 18);
  doc.setFontSize(9);
  doc.setTextColor(100);
  const filterLine = describeFilters(filters);
  doc.text(doc.splitTextToSize(filterLine, 180), 14, 25);
  doc.text(`Generated ${new Date().toLocaleString()}  |  ${rows.length} ${rows.length === 1 ? 'county' : 'counties'} matched`, 14, 32);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 40,
    head: [['County', 'Median Sale Price', 'Days on Market', 'New Listings', 'Closed Sales', 'Sale/List %', 'Hot/Cold']],
    body: rows.map(({ record, insights }) => [
      record.geoName,
      record.resolved.medianSalePrice != null ? `$${record.resolved.medianSalePrice.toLocaleString()}` : '--',
      record.resolved.medianDaysOnMarket ?? '--',
      record.resolved.newListings ?? '--',
      record.resolved.closedSales ?? '--',
      record.resolved.saleToListRatioPct != null ? `${round(record.resolved.saleToListRatioPct, 1)}%` : '--',
      `${insights.hotColdScore.label} (${round(insights.hotColdScore.value, 0)})`,
    ]),
    styles: { fontSize: 7 },
    headStyles: { fillColor: [30, 41, 59] },
  });

  const afterTableY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(
    doc.splitTextToSize(
      'Figures are derived by MAY Assistant from public source data (see in-app source list) and are not official published figures. Price-per-square-foot is not yet available in this dataset.',
      180,
    ),
    14,
    afterTableY,
  );

  doc.save(filename);
}
